import { useEffect, useMemo, useState } from 'react';
import { MAIN_NAV_HEADER_ID } from '../components/header/Header';

export type ScrollListener = {
    isScrolled: boolean;
    isNotScrolled: boolean;
    scrollY: number;
};

/**
 * - Do not call preventDefault() inside onScroll
 * - Avoid manipulating DOM inside onScroll
 *
 * @param onScroll - Callback function that will be called with the scroll listener data
 */
export const useScrollListener = (onScroll?: (data: ScrollListener) => void): ScrollListener => {
    const [data, setData] = useState<ScrollListener>({
        isScrolled: false,
        isNotScrolled: true,
        scrollY: 0,
    });
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        const height =
            document.getElementById(MAIN_NAV_HEADER_ID)?.getBoundingClientRect().height ?? 0;
        setHeaderHeight(height);
    }, []);

    useEffect(() => {
        const handleScroll = (): void => {
            const newHeaderHeight =
                document.getElementById(MAIN_NAV_HEADER_ID)?.getBoundingClientRect().height ?? 0;

            // When the header height changes it triggers scroll,
            // but since it is not the user scrolling, do not update
            // scroll direction booleans to avoid UI flickering.
            if (headerHeight !== newHeaderHeight) {
                setHeaderHeight(newHeaderHeight);
                return;
            }

            const result = {
                isScrolled: window.scrollY > 0,
                isNotScrolled: window.scrollY <= 0,
                scrollY: window.scrollY,
            };

            setData(result);

            if (onScroll) {
                onScroll(result);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return (): void => window.removeEventListener('scroll', handleScroll);
    }, [data, onScroll, headerHeight]);

    return useMemo(() => data, [data]);
};
